import { Link } from "react-router-dom";
import { MapPin, Users, Package, Calendar, ArrowRight, Route as RouteIcon } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { UserIdentity } from "@/components/UserIdentity";
import { Badge } from "@/components/ui/badge";

export const RouteLine = ({ origin, stops = [], destination, compact = false }) => {
  const cities = [origin, ...stops, destination];
  return (
    <div className="flex items-center flex-wrap gap-y-1" data-testid="route-line">
      {cities.map((c, i) => (
        <span key={i} className="flex items-center">
          <span className={`font-medium ${i === 0 || i === cities.length - 1 ? "text-primary" : "text-slate-500"} ${compact ? "text-xs" : "text-sm"}`}>{c}</span>
          {i < cities.length - 1 && <ArrowRight size={compact ? 12 : 14} className="mx-1.5 text-slate-300" />}
        </span>
      ))}
    </div>
  );
};

export const AcceptsIcons = ({ accepts }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2" data-testid="accepts-icons">
      {(accepts === "passengers" || accepts === "both") && (
        <span title={t("trip.acceptsPassengers")} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-medium"><Users size={13} />{t("publish.passengers")}</span>
      )}
      {(accepts === "parcels" || accepts === "both") && (
        <span title={t("trip.acceptsParcels")} className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full font-medium"><Package size={13} />{t("publish.parcels")}</span>
      )}
    </div>
  );
};

export const TripCard = ({ trip }) => {
  const { t } = useLanguage();
  return (
    <Link to={`/viagem/${trip.id}`} data-testid={`trip-card-${trip.id}`} className="block bg-white border border-slate-100 rounded-2xl p-5 shadow-sm hover:shadow-[0_12px_40px_rgba(15,41,66,0.12)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        {trip.percurso ? (
          <p className="text-sm font-medium text-primary flex items-center gap-1.5" data-testid="trip-percurso"><RouteIcon size={14} className="text-accent shrink-0" />{trip._tr?.percurso || trip.percurso}</p>
        ) : (
          <RouteLine origin={trip.origin} stops={trip.stops} destination={trip.destination} />
        )}
        {trip.partial && <Badge data-testid="partial-badge" className="bg-amber-100 text-amber-800 hover:bg-amber-100 shrink-0">{t("search.partial")}</Badge>}
      </div>
      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
        <span className="flex items-center gap-1.5"><Calendar size={14} />{trip.date}{trip.time ? ` · ${trip.time}` : ""}</span>
        {trip.accepts !== "parcels" && <span className="flex items-center gap-1.5"><Users size={14} />{Math.max(0, trip.seats - (trip.seats_taken || 0))} {t("trip.seats")}</span>}
      </div>
      <div className="flex items-center justify-between">
        <UserIdentity user={trip.driver} testid={`driver-identity-${trip.id}`} avatarTestid={`driver-avatar-${trip.id}`} />
        <div className="text-right">
          <AcceptsIcons accepts={trip.accepts} />
          <p className="text-sm font-bold text-primary mt-1.5">{trip.price > 0 ? `${trip.price}€ ${t("trip.price")}` : t("trip.toArrange")}</p>
        </div>
      </div>
    </Link>
  );
};

export const ParcelCard = ({ parcel }) => {
  const { t } = useLanguage();
  const sizeLabel = { s: t("parcel.sizeS"), m: t("parcel.sizeM"), l: t("parcel.sizeL") }[parcel.size] || parcel.size;
  return (
    <Link to={`/encomenda/${parcel.id}`} data-testid={`parcel-card-${parcel.id}`} className="block bg-white border border-slate-100 rounded-2xl p-5 shadow-sm hover:shadow-[0_12px_40px_rgba(15,41,66,0.12)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-primary">
        <MapPin size={14} className="text-slate-400" />{parcel.origin}
        <ArrowRight size={14} className="text-slate-300" />
        <MapPin size={14} className="text-accent" />{parcel.destination}
      </div>
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{parcel._tr?.description || parcel.description}</p>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><Package size={13} />{sizeLabel} · {parcel.weight}kg</span>
        <span className="flex items-center gap-1.5"><Calendar size={13} />{t("parcel.deliverBy")} {parcel.date}</span>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-500 flex items-center gap-1.5">{t("parcel.sender")}: <UserIdentity user={parcel.sender} avatarSize="w-5 h-5" avatarText="text-[10px]" nameClass="text-xs font-semibold text-slate-700" testid={`sender-identity-${parcel.id}`} /></span>
        <span className="text-sm font-bold text-accent">{parcel.price_offer > 0 ? `${parcel.price_offer}€` : t("trip.toArrange")}</span>
      </div>
    </Link>
  );
};
